import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Check, X, ChevronLeft, ChevronRight, Save, Pin, GripHorizontal, Trash2, Pencil, Maximize2, Minimize2 } from 'lucide-react';
import { useMarkingStore } from '../../stores/markingStore';
import { useAuthStore } from '../../stores/authStore';
import { generateManualMarkedPdf } from '../../utils/manualMarkingPdf';

let pdfJsLoaderPromise = null;
const PEN_COLORS = [
  { value: '#dc2626', label: 'Red' },
  { value: '#2563eb', label: 'Blue' },
  { value: '#16a34a', label: 'Green' },
  { value: '#111827', label: 'Black' }
];
const ensurePdfJs = () => {
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (pdfJsLoaderPromise) return pdfJsLoaderPromise;

  pdfJsLoaderPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector('script[data-pdfjs-loader="true"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.pdfjsLib), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Failed to load PDF renderer.')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.async = true;
    script.dataset.pdfjsLoader = 'true';
    script.onload = () => {
      if (!window.pdfjsLib) {
        reject(new Error('PDF renderer unavailable.'));
        return;
      }
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      resolve(window.pdfjsLib);
    };
    script.onerror = () => reject(new Error('Failed to load PDF renderer.'));
    document.body.appendChild(script);
  });

  return pdfJsLoaderPromise;
};

const buildGradeEntriesFromQuestions = (questions = []) => {
  const initialGrades = [];

  questions.forEach((q, index) => {
    const qLabel = String(q.questionNumber || '').trim();
    if (!qLabel) return;
    const subquestions = Array.isArray(q.subquestions) ? q.subquestions : [];
    if (subquestions.length === 0) {
      initialGrades.push({
        id: q.id || `q-${qLabel}-${index}`,
        questionNumber: qLabel,
        learnerScore: 0,
        totalMarks: Number(q.marks) || 0,
        percentage: 0,
        annotation: '',
        subject: q.subject || '',
        grade: q.grade || '',
        topic: q.topic || '',
        subtopics: q.subtopics || [],
        concepts: q.concepts || []
      });
      return;
    }

    subquestions.forEach((sq, sqIndex) => {
      const subLabel = String(sq.label || '').trim();
      if (!subLabel) return;
      const full = subLabel.startsWith(qLabel) ? subLabel : `${qLabel}.${subLabel.replace(/^\./, '')}`;
      initialGrades.push({
        id: `${q.id || `q-${qLabel}-${index}`}-sq-${sqIndex}`,
        questionNumber: full,
        learnerScore: 0,
        totalMarks: Number(sq.marks) || 0,
        percentage: 0,
        annotation: '',
        subject: q.subject || '',
        grade: q.grade || '',
        topic: q.topic || '',
        subtopics: q.subtopics || [],
        concepts: q.concepts || []
      });
    });
  });

  return initialGrades;
};

const ManualMarkingWorkspace = ({ jobId, onBack, onCompleted }) => {
  const { user } = useAuthStore();
  const { currentJob, loadMarkingJob, getMarkedPdfUrl, saveManualResults, markingQuestions, loadMarkingQuestions } = useMarkingStore();
  const [scriptUrls, setScriptUrls] = useState([]);
  const [isScriptLoading, setIsScriptLoading] = useState(false);
  const [scriptLoadError, setScriptLoadError] = useState('');
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [imageLoadError, setImageLoadError] = useState('');
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [currentLearnerIndex, setCurrentLearnerIndex] = useState(0);
  const [activeMarker, setActiveMarker] = useState(null);
  const [selectedGradeId, setSelectedGradeId] = useState(null);
  const [placedIcons, setPlacedIcons] = useState({});
  const [placedAnnotations, setPlacedAnnotations] = useState({});
  const [penStrokes, setPenStrokes] = useState({});
  const [penColor, setPenColor] = useState(PEN_COLORS[0].value);
  const [markerPadPosition, setMarkerPadPosition] = useState({ x: 24, y: 180 });
  const [isMarkerPadPinned, setIsMarkerPadPinned] = useState(false);
  const [isScriptFullscreen, setIsScriptFullscreen] = useState(false);
  const [draggingItem, setDraggingItem] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [questionNumber, setQuestionNumber] = useState('');
  const [learnerScore, setLearnerScore] = useState('');
  const [totalMarks, setTotalMarks] = useState('');
  const [annotation, setAnnotation] = useState('');
  const [grades, setGrades] = useState([]);

  const [pdfPageCount, setPdfPageCount] = useState(0);
  const [pdfPageImageUrl, setPdfPageImageUrl] = useState('');
  const [pdfLoadError, setPdfLoadError] = useState('');
  const [isPdfPageLoading, setIsPdfPageLoading] = useState(false);

  const markerPadRef = useRef(null);
  const workspaceRef = useRef(null);
  const pageLayerRef = useRef(null);
  const padDragMetaRef = useRef({ dragging: false, offsetX: 0, offsetY: 0 });
  const itemDragMetaRef = useRef(null);
  const dragFrameRef = useRef(null);
  const pendingPointRef = useRef(null);
  const pdfDocRef = useRef(null);
  const scriptImageRef = useRef(null);
  const activePenStrokeRef = useRef(null);
  const initializedQuestionSeedRef = useRef(null);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement;
      setIsScriptFullscreen(fullscreenElement === workspaceRef.current);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const toggleScriptFullscreen = async () => {
    try {
      const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement;
      if (fullscreenElement) {
        const exitFullscreen = document.exitFullscreen || document.webkitExitFullscreen;
        await exitFullscreen?.call(document);
        return;
      }

      const requestFullscreen = workspaceRef.current?.requestFullscreen || workspaceRef.current?.webkitRequestFullscreen;
      if (!requestFullscreen) {
        setFormError('Fullscreen mode is not supported by this browser.');
        return;
      }
      await requestFullscreen.call(workspaceRef.current);
      setFormError('');
    } catch (error) {
      console.error('Failed to enter fullscreen mode:', error);
      setFormError('Unable to enter fullscreen mode. Please try again.');
    }
  };

  useEffect(() => {
    if (jobId && (!currentJob || currentJob.id !== jobId)) {
      loadMarkingJob(jobId);
    }
  }, [jobId, currentJob, loadMarkingJob]);

  useEffect(() => {
    initializedQuestionSeedRef.current = null;
    setGrades([]);
    setSelectedGradeId(null);
  }, [jobId]);

  useEffect(() => {
    const ownerId = currentJob?.userId || user?.uid;
    if (!ownerId) return;
    loadMarkingQuestions(ownerId).catch((err) => {
      console.error('Failed to load marking questions:', err);
    });
  }, [currentJob?.userId, user?.uid, loadMarkingQuestions]);

  useEffect(() => {
    if (!currentJob?.scriptFiles?.length) {
      setScriptUrls([]);
      setIsScriptLoading(false);
      setScriptLoadError('No script files are attached to this marking job.');
      return;
    }

    let cancelled = false;
    setScriptUrls([]);
    setIsScriptLoading(true);
    setScriptLoadError('');

    const invalidFileIndex = currentJob.scriptFiles.findIndex((file) => (
      !file || typeof file !== 'object' || typeof file.path !== 'string' || !file.path.trim()
    ));

    if (invalidFileIndex !== -1) {
      setIsScriptLoading(false);
      setScriptLoadError(`Script file ${invalidFileIndex + 1} has invalid or missing storage metadata.`);
      return () => {
        cancelled = true;
      };
    }

    Promise.all(currentJob.scriptFiles.map((file) => getMarkedPdfUrl(file.path.trim())))
      .then((urls) => {
        if (!cancelled) {
          setScriptUrls(urls);
          setIsScriptLoading(false);
        }
      })
      .catch((err) => {
        console.error('Failed to load script files:', err);
        if (!cancelled) {
          setIsScriptLoading(false);
          setScriptLoadError(err?.message || 'Failed to load script files from storage.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [currentJob?.scriptFiles, getMarkedPdfUrl]);

  const selectedGrade = useMemo(
    () => grades.find((g) => g.id === selectedGradeId) || null,
    [grades, selectedGradeId]
  );
  const isBatchQuestionJob = currentJob?.mode === 'batch-question';
  const batchQueue = useMemo(() => {
    if (!isBatchQuestionJob) return [];

    const entries = Array.isArray(currentJob?.scriptEntries) ? currentJob.scriptEntries : [];
    const queue = Array.isArray(currentJob?.batchQueue) ? currentJob.batchQueue : [];
    const source = entries.length > 0 ? entries : queue;

    if (source.length === 0 && Array.isArray(currentJob?.scriptFiles)) {
      return currentJob.scriptFiles.map((_, index) => ({
        index,
        learnerId: null,
        learnerName: `Learner ${index + 1}`,
        learnerNumber: null
      }));
    }

    return [...source]
      .map((item, index) => ({
        index: Number.isFinite(Number(item.index)) ? Number(item.index) : index,
        learnerId: item.learnerId || null,
        learnerName: item.learnerName || `Learner ${index + 1}`,
        learnerNumber: item.learnerNumber || null
      }))
      .sort((a, b) => a.index - b.index);
  }, [currentJob?.batchQueue, currentJob?.scriptEntries, currentJob?.scriptFiles, isBatchQuestionJob]);
  const hasTaskData = Boolean(currentJob?.taskData?.questions?.length);
  const targetSubject = String(currentJob?.taskData?.subject || currentJob?.subject || '').trim().toLowerCase();
  const targetGrade = String(currentJob?.taskData?.grade || currentJob?.grade || '').trim().toLowerCase();

  const filteredMarkingQuestions = useMemo(() => {
    if (!markingQuestions.length) return [];
    if (!targetSubject && !targetGrade) return markingQuestions;

    const matches = markingQuestions.filter((question) => {
      const questionSubject = String(question.subject || '').trim().toLowerCase();
      const questionGrade = String(question.grade || '').trim().toLowerCase();
      const subjectMatches = !targetSubject || questionSubject === targetSubject;
      const gradeMatches = !targetGrade || questionGrade === targetGrade;
      return subjectMatches && gradeMatches;
    });

    return matches.length > 0 ? matches : markingQuestions;
  }, [markingQuestions, targetGrade, targetSubject]);

  const isQuestionFilterFallback = Boolean(
    markingQuestions.length > 0 &&
    filteredMarkingQuestions.length > 0 &&
    filteredMarkingQuestions.length === markingQuestions.length &&
    (targetSubject || targetGrade) &&
    !markingQuestions.some((question) => {
      const questionSubject = String(question.subject || '').trim().toLowerCase();
      const questionGrade = String(question.grade || '').trim().toLowerCase();
      const subjectMatches = !targetSubject || questionSubject === targetSubject;
      const gradeMatches = !targetGrade || questionGrade === targetGrade;
      return subjectMatches && gradeMatches;
    })
  );

  const isPdfJob = Boolean(
    !isBatchQuestionJob &&
    currentJob?.scriptFiles?.length === 1 &&
    (currentJob?.scriptFiles?.[0]?.mimeType || '').includes('pdf')
  );

  const totalDisplayPages = isPdfJob ? Math.max(pdfPageCount, 1) : Math.max(scriptUrls.length, 1);
  const activeCanvasIndex = isBatchQuestionJob ? currentLearnerIndex : currentPageIndex;
  const activeDisplayCount = isBatchQuestionJob ? Math.max(scriptUrls.length, 1) : totalDisplayPages;

  const clampToRange = (value, min, max) => Math.max(min, Math.min(max, value));

  const getPagePointFromClient = useCallback((clientX, clientY) => {
    if (!pageLayerRef.current) return null;
    const rect = pageLayerRef.current.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;
    return {
      x: clampToRange(x, 0, 100),
      y: clampToRange(y, 0, 100)
    };
  }, []);

  const handlePadMouseMove = useCallback((e) => {
    if (isMarkerPadPinned || !padDragMetaRef.current.dragging || !markerPadRef.current) return;
    const width = markerPadRef.current.offsetWidth;
    const height = markerPadRef.current.offsetHeight;
    const nextX = clampToRange(e.clientX - padDragMetaRef.current.offsetX, 8, window.innerWidth - width - 8);
    const nextY = clampToRange(e.clientY - padDragMetaRef.current.offsetY, 72, window.innerHeight - height - 8);
    setMarkerPadPosition({ x: nextX, y: nextY });
  }, [isMarkerPadPinned]);

  const handlePadMouseUp = useCallback(() => {
    padDragMetaRef.current.dragging = false;
    window.removeEventListener('mousemove', handlePadMouseMove);
    window.removeEventListener('mouseup', handlePadMouseUp);
  }, [handlePadMouseMove]);

  useEffect(() => () => {
    window.removeEventListener('mousemove', handlePadMouseMove);
    window.removeEventListener('mouseup', handlePadMouseUp);
  }, [handlePadMouseMove, handlePadMouseUp]);

  useEffect(() => {
    setCurrentPageIndex((idx) => Math.min(idx, totalDisplayPages - 1));
  }, [totalDisplayPages]);

  useEffect(() => {
    if (!isBatchQuestionJob) {
      setCurrentLearnerIndex(0);
      return;
    }
    setCurrentLearnerIndex((idx) => Math.min(idx, Math.max(scriptUrls.length, 1) - 1));
  }, [isBatchQuestionJob, scriptUrls.length]);

  useEffect(() => {
    const questionSource = hasTaskData ? (currentJob?.taskData?.questions || []) : filteredMarkingQuestions;
    const sourceType = hasTaskData ? 'task' : 'bank';
    const seedKey = `${jobId}:${sourceType}:${questionSource.map((q) => q.id || q.questionNumber || '').join('|')}`;
    const initialGrades = buildGradeEntriesFromQuestions(questionSource);

    if (initialGrades.length === 0) return;
    if (initializedQuestionSeedRef.current === seedKey) return;

    setGrades((prev) => {
      if (prev.length > 0 && initializedQuestionSeedRef.current) return prev;
      return initialGrades;
    });
    setSelectedGradeId((prev) => prev || initialGrades[0]?.id || null);
    initializedQuestionSeedRef.current = seedKey;
  }, [jobId, hasTaskData, currentJob?.taskData, filteredMarkingQuestions]);

  useEffect(() => {
    if (hasTaskData || !selectedGrade) return;
    setQuestionNumber(selectedGrade.questionNumber || '');
    setLearnerScore(String(selectedGrade.learnerScore ?? ''));
    setTotalMarks(String(selectedGrade.totalMarks ?? ''));
    setAnnotation(selectedGrade.annotation || '');
  }, [hasTaskData, selectedGrade]);

  useEffect(() => {
    if (!isPdfJob || !scriptUrls[0]) {
      setPdfPageCount(0);
      setPdfPageImageUrl('');
      setPdfLoadError('');
      if (pdfDocRef.current) {
        try {
          pdfDocRef.current.destroy();
        } catch (_) {}
        pdfDocRef.current = null;
      }
      return;
    }

    let cancelled = false;
    const loadPdf = async () => {
      setPdfLoadError('');
      setIsPdfPageLoading(true);
      try {
        const pdfjsLib = await ensurePdfJs();
        const loadingTask = pdfjsLib.getDocument(scriptUrls[0]);
        const pdfDoc = await loadingTask.promise;
        if (cancelled) {
          pdfDoc.destroy();
          return;
        }
        if (pdfDocRef.current) {
          try {
            pdfDocRef.current.destroy();
          } catch (_) {}
        }
        pdfDocRef.current = pdfDoc;
        setPdfPageCount(pdfDoc.numPages || 1);
      } catch (err) {
        console.error('Failed to load PDF for manual marking:', err);
        if (!cancelled) {
          setPdfPageCount(1);
          setPdfLoadError('Failed to render PDF. Please refresh and try again.');
        }
      } finally {
        if (!cancelled) setIsPdfPageLoading(false);
      }
    };

    loadPdf();
    return () => {
      cancelled = true;
    };
  }, [isPdfJob, scriptUrls]);

  useEffect(() => {
    if (!isPdfJob || !pdfDocRef.current) return;
    let cancelled = false;

    const renderPdfPage = async () => {
      setPdfLoadError('');
      setIsPdfPageLoading(true);
      try {
        const pdfDoc = pdfDocRef.current;
        const pageNum = Math.min(Math.max(currentPageIndex + 1, 1), pdfDoc.numPages || 1);
        const page = await pdfDoc.getPage(pageNum);
        const baseViewport = page.getViewport({ scale: 1 });
        const scale = Math.max(1, 1400 / baseViewport.width);
        const viewport = page.getViewport({ scale });

        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d');
        await page.render({ canvasContext: ctx, viewport }).promise;

        if (!cancelled) {
          setPdfPageImageUrl(canvas.toDataURL('image/png'));
        }
      } catch (err) {
        console.error('Failed to render PDF page:', err);
        if (!cancelled) setPdfLoadError('Failed to render this PDF page.');
      } finally {
        if (!cancelled) setIsPdfPageLoading(false);
      }
    };

    renderPdfPage();
    return () => {
      cancelled = true;
    };
  }, [isPdfJob, currentPageIndex, pdfPageCount]);

  useEffect(() => {
    if (isPdfJob) {
      setIsImageLoading(false);
      setImageLoadError('');
      return;
    }

    const imageUrl = scriptUrls[activeCanvasIndex];
    setImageLoadError('');

    if (!imageUrl) {
      setIsImageLoading(false);
      return;
    }

    const image = scriptImageRef.current;
    if (image?.complete) {
      setIsImageLoading(false);
      if (image.naturalWidth === 0) {
        setImageLoadError(`Failed to display script image ${activeCanvasIndex + 1}.`);
      }
      return;
    }

    setIsImageLoading(true);
  }, [activeCanvasIndex, isPdfJob, scriptUrls]);

  const handleCanvasClick = (e) => {
    if (draggingItem) return;
    if (activeMarker === 'pen') return;
    const point = getPagePointFromClient(e.clientX, e.clientY);
    if (!point) return;

    if (activeMarker) {
      if (!selectedGradeId) {
        setFormError('Select a question first before placing markers.');
        return;
      }
      const icon = { id: Date.now(), type: activeMarker, color: penColor, gradeId: selectedGradeId, ...point };
      setPlacedIcons((prev) => {
        const page = prev[activeCanvasIndex] || [];
        return { ...prev, [activeCanvasIndex]: [...page, icon] };
      });
      if (activeMarker === 'tick') {
        setGrades((prev) => prev.map((g) => {
          if (g.id !== selectedGradeId) return g;
          const max = Number(g.totalMarks) || 0;
          const next = Math.max(0, Math.min(max, (Number(g.learnerScore) || 0) + 1));
          const pct = max > 0 ? Math.round((next / max) * 10000) / 100 : 0;
          return { ...g, learnerScore: next, percentage: pct };
        }));
      }
      return;
    }

    if (selectedGrade) {
      const ann = { id: Date.now(), gradeId: selectedGrade.id, ...point };
      setPlacedAnnotations((prev) => {
        const page = prev[activeCanvasIndex] || [];
        return { ...prev, [activeCanvasIndex]: [...page, ann] };
      });
      setFormError('');
      return;
    }

  };

  const handlePenPointerDown = (e) => {
    if (activeMarker !== 'pen') return;
    const point = getPagePointFromClient(e.clientX, e.clientY);
    if (!point) return;

    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const stroke = {
      id: `${Date.now()}-${e.pointerId}`,
      color: penColor,
      points: [point]
    };
    activePenStrokeRef.current = {
      pageIndex: activeCanvasIndex,
      pointerId: e.pointerId,
      strokeId: stroke.id
    };
    setPenStrokes((prev) => ({
      ...prev,
      [activeCanvasIndex]: [...(prev[activeCanvasIndex] || []), stroke]
    }));
  };

  const handlePenPointerMove = (e) => {
    const activeStroke = activePenStrokeRef.current;
    if (!activeStroke || activeStroke.pointerId !== e.pointerId) return;
    const point = getPagePointFromClient(e.clientX, e.clientY);
    if (!point) return;

    e.preventDefault();
    setPenStrokes((prev) => ({
      ...prev,
      [activeStroke.pageIndex]: (prev[activeStroke.pageIndex] || []).map((stroke) => (
        stroke.id === activeStroke.strokeId
          ? { ...stroke, points: [...stroke.points, point] }
          : stroke
      ))
    }));
  };

  const handlePenPointerUp = (e) => {
    if (activePenStrokeRef.current?.pointerId !== e.pointerId) return;
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    activePenStrokeRef.current = null;
  };

  const handleRemoveLastPenStroke = () => {
    setPenStrokes((prev) => ({
      ...prev,
      [activeCanvasIndex]: (prev[activeCanvasIndex] || []).slice(0, -1)
    }));
  };

  const updateDraggedItemPosition = useCallback((point, item) => {
    if (!item) return;
    if (item.kind === 'icon') {
      setPlacedIcons((prev) => ({
        ...prev,
        [activeCanvasIndex]: (prev[activeCanvasIndex] || []).map((icon) => (
          icon.id === item.id ? { ...icon, x: point.x, y: point.y } : icon
        ))
      }));
      return;
    }
    setPlacedAnnotations((prev) => ({
      ...prev,
      [activeCanvasIndex]: (prev[activeCanvasIndex] || []).map((ann) => (
        ann.id === item.id ? { ...ann, x: point.x, y: point.y } : ann
      ))
    }));
  }, [activeCanvasIndex]);

  const handleItemMouseMove = useCallback((e) => {
    if (!itemDragMetaRef.current) return;
    const rawPoint = getPagePointFromClient(e.clientX, e.clientY);
    if (!rawPoint) return;

    const adjustedPoint = {
      x: clampToRange(rawPoint.x - itemDragMetaRef.current.offsetXPct, 0, 100),
      y: clampToRange(rawPoint.y - itemDragMetaRef.current.offsetYPct, 0, 100)
    };

    pendingPointRef.current = adjustedPoint;
    if (!dragFrameRef.current) {
      dragFrameRef.current = window.requestAnimationFrame(() => {
        if (pendingPointRef.current && itemDragMetaRef.current) {
          updateDraggedItemPosition(pendingPointRef.current, itemDragMetaRef.current);
        }
        dragFrameRef.current = null;
      });
    }
  }, [getPagePointFromClient, updateDraggedItemPosition]);

  const handleItemMouseUp = useCallback(() => {
    itemDragMetaRef.current = null;
    pendingPointRef.current = null;
    if (dragFrameRef.current) {
      window.cancelAnimationFrame(dragFrameRef.current);
      dragFrameRef.current = null;
    }
    setDraggingItem(null);
    window.removeEventListener('mousemove', handleItemMouseMove);
    window.removeEventListener('mouseup', handleItemMouseUp);
  }, [handleItemMouseMove]);

  const handleStartItemDrag = (e, item) => {
    if (!pageLayerRef.current) return;
    e.preventDefault();
    e.stopPropagation();

    const point = getPagePointFromClient(e.clientX, e.clientY);
    if (!point) return;

    const offsetXPct = point.x - item.x;
    const offsetYPct = point.y - item.y;
    itemDragMetaRef.current = {
      kind: item.kind,
      id: item.id,
      offsetXPct,
      offsetYPct
    };
    setDraggingItem({ kind: item.kind, id: item.id });
    window.addEventListener('mousemove', handleItemMouseMove);
    window.addEventListener('mouseup', handleItemMouseUp);
  };

  const handleRemoveIcon = (id) => {
    const iconToRemove = (placedIcons[activeCanvasIndex] || []).find((icon) => icon.id === id);
    if (iconToRemove?.type === 'tick' && iconToRemove.gradeId) {
      setGrades((prev) => prev.map((g) => {
        if (g.id !== iconToRemove.gradeId) return g;
        const max = Number(g.totalMarks) || 0;
        const next = Math.max(0, Math.min(max, (Number(g.learnerScore) || 0) - 1));
        const pct = max > 0 ? Math.round((next / max) * 10000) / 100 : 0;
        return { ...g, learnerScore: next, percentage: pct };
      }));
    }
    setPlacedIcons((prev) => ({
      ...prev,
      [activeCanvasIndex]: (prev[activeCanvasIndex] || []).filter((icon) => icon.id !== id)
    }));
  };

  const handleRemoveAnnotation = (id) => {
    setPlacedAnnotations((prev) => ({
      ...prev,
      [activeCanvasIndex]: (prev[activeCanvasIndex] || []).filter((ann) => ann.id !== id)
    }));
  };

  const handleSaveGrade = () => {
    const score = Number(learnerScore);
    const total = Number(totalMarks);
    if (!questionNumber.trim()) return setFormError('Question number is required.');
    if (!Number.isFinite(score) || !Number.isFinite(total) || total <= 0 || score < 0 || score > total) {
      return setFormError('Use valid numbers and ensure score is between 0 and total.');
    }
    const percentage = Math.round((score / total) * 10000) / 100;
    let nextSelectedId = null;
    setGrades((prev) => {
      const existingId = selectedGradeId && prev.some((g) => g.id === selectedGradeId) ?
        selectedGradeId :
        prev.find((g) => g.questionNumber === questionNumber.trim())?.id;

      if (existingId) {
        nextSelectedId = existingId;
        return prev.map((grade) => (
          grade.id === existingId ? {
            ...grade,
            questionNumber: questionNumber.trim(),
            learnerScore: score,
            totalMarks: total,
            percentage,
            annotation: annotation.trim()
          } : grade
        ));
      }

      return [
        ...prev,
        {
          id: (() => {
            const createdId = Date.now();
            nextSelectedId = createdId;
            return createdId;
          })(),
          questionNumber: questionNumber.trim(),
          learnerScore: score,
          totalMarks: total,
          percentage,
          annotation: annotation.trim()
        }
      ];
    });
    setQuestionNumber('');
    setLearnerScore('');
    setTotalMarks('');
    setAnnotation('');
    setSelectedGradeId(nextSelectedId);
    setActiveMarker(null);
    setFormError('');
  };

  const handleFinalize = async () => {
    if (grades.length === 0) {
      setFormError('Add at least one grade before finalizing.');
      return;
    }

    setIsSaving(true);
    setFormError('');
    try {
      const totalScore = grades.reduce((sum, g) => sum + g.learnerScore, 0);
      const maxScore = grades.reduce((sum, g) => sum + g.totalMarks, 0);
      const percentage = maxScore > 0 ? Math.round((totalScore / maxScore) * 10000) / 100 : 0;
      const pageCountForResults = isBatchQuestionJob ? Math.max(scriptUrls.length, 1) : totalDisplayPages;

      const annotationById = new Map(grades.map((g) => [g.id, g]));
      const pageResults = Array.from({ length: pageCountForResults }, (_, idx) => {
        const entries = (placedAnnotations[idx] || []).map((ann) => {
          const grade = annotationById.get(ann.gradeId);
          const score = grade?.learnerScore || 0;
          const total = grade?.totalMarks || 0;
          const type = score === total ? 'tick' : score === 0 ? 'cross' : 'half-tick';
          return {
            xPercent: ann.x,
            yPercent: ann.y,
            type,
            questionLabel: grade?.questionNumber || '-',
            marksAwarded: score,
            marksAvailable: total,
            comment: grade?.annotation || '',
            confidence: 'high'
          };
        });
        return { pageNumber: idx + 1, annotations: entries };
      });

      const markerIcons = Array.from({ length: pageCountForResults }, (_, idx) => ({
        pageNumber: idx + 1,
        icons: (placedIcons[idx] || []).map((icon) => ({
          type: icon.type,
          color: icon.color || '#dc2626',
          xPercent: icon.x,
          yPercent: icon.y
        }))
      }));

      const drawingStrokes = Array.from({ length: pageCountForResults }, (_, idx) => ({
        pageNumber: idx + 1,
        strokes: (penStrokes[idx] || []).map((stroke) => ({
          color: stroke.color,
          points: stroke.points.map((point) => ({
            xPercent: point.x,
            yPercent: point.y
          }))
        }))
      }));

      const resultData = {
        totalScore,
        maxScore,
        percentage,
        pageResults,
        markerIcons,
        penStrokes: drawingStrokes,
        taskData: currentJob?.taskData || null,
        batchQueue: isBatchQuestionJob ? batchQueue : null
      };
      const markedPdfBytes = await generateManualMarkedPdf({
        scriptFiles: currentJob?.scriptFiles || [],
        resultData,
        resolveDownloadUrl: getMarkedPdfUrl
      });
      await saveManualResults(jobId, resultData, markedPdfBytes, user?.uid || currentJob?.userId);
      if (onCompleted) onCompleted();
    } catch (err) {
      console.error('Failed to save manual results:', err);
      setFormError(err?.message || 'Failed to save manual results.');
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => () => {
    if (dragFrameRef.current) {
      window.cancelAnimationFrame(dragFrameRef.current);
    }
    window.removeEventListener('mousemove', handleItemMouseMove);
    window.removeEventListener('mouseup', handleItemMouseUp);
  }, [handleItemMouseMove, handleItemMouseUp]);

  useEffect(() => () => {
    if (pdfDocRef.current) {
      try {
        pdfDocRef.current.destroy();
      } catch (_) {}
      pdfDocRef.current = null;
    }
  }, []);

  if (!currentJob) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sigma-600 mx-auto" />
      </div>
    );
  }

  const currentIcons = placedIcons[activeCanvasIndex] || [];
  const currentAnnotations = placedAnnotations[activeCanvasIndex] || [];
  const currentPenStrokes = penStrokes[activeCanvasIndex] || [];
  const displayImageUrl = isPdfJob ? pdfPageImageUrl : scriptUrls[activeCanvasIndex];
  const activeLearner = isBatchQuestionJob ? (batchQueue[activeCanvasIndex] || null) : null;
  const manualQuestionOptions = grades
    .slice()
    .sort((a, b) => String(a.questionNumber || '').localeCompare(String(b.questionNumber || '')));

  return (
    <div
      ref={workspaceRef}
      className={`flex h-full min-h-0 flex-col gap-2 ${isScriptFullscreen ? 'bg-gray-100 p-2' : ''}`}
    >
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Marking
        </button>
        <button
          onClick={handleFinalize}
          disabled={isSaving}
          className="inline-flex items-center px-3 py-2 bg-sigma-600 text-white text-sm rounded-md hover:bg-sigma-700 disabled:opacity-60"
        >
          <Save className="w-4 h-4 mr-1.5" />
          {isSaving ? 'Saving...' : 'Finalize Manual Marking'}
        </button>
      </div>

      <div
        ref={markerPadRef}
        className={`fixed z-30 rounded-xl border border-gray-200 bg-white/95 shadow-lg backdrop-blur-sm ${
          isMarkerPadPinned ? 'cursor-default' : 'cursor-move'
        }`}
        style={{ top: markerPadPosition.y, left: markerPadPosition.x }}
      >
        <div
          className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-200"
          onMouseDown={(e) => {
            if (isMarkerPadPinned || !markerPadRef.current) return;
            padDragMetaRef.current.dragging = true;
            const rect = markerPadRef.current.getBoundingClientRect();
            padDragMetaRef.current.offsetX = e.clientX - rect.left;
            padDragMetaRef.current.offsetY = e.clientY - rect.top;
            window.addEventListener('mousemove', handlePadMouseMove);
            window.addEventListener('mouseup', handlePadMouseUp);
          }}
        >
          <div className="inline-flex items-center text-xs font-medium text-gray-600">
            <GripHorizontal className="w-4 h-4 mr-1.5" />
            Marker Pad
          </div>
          <button
            type="button"
            onClick={() => setIsMarkerPadPinned((p) => !p)}
            className={`inline-flex items-center text-xs px-2 py-1 rounded-md border ${
              isMarkerPadPinned
                ? 'border-sigma-300 bg-sigma-50 text-sigma-700'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Pin className={`w-3.5 h-3.5 mr-1 ${isMarkerPadPinned ? 'rotate-45' : ''}`} />
            {isMarkerPadPinned ? 'Unpin' : 'Pin'}
          </button>
        </div>
        <div className="flex items-center gap-2 p-3">
          <button
            type="button"
            onClick={() => {
              setActiveMarker((m) => (m === 'tick' ? null : 'tick'));
            }}
            className={`px-3 py-2 rounded-lg text-sm border inline-flex items-center ${
              activeMarker === 'tick' ? 'bg-green-50 border-green-300 text-green-700' : 'border-gray-300 text-gray-700'
            }`}
          >
            <Check className="w-4 h-4 mr-1.5" /> Tick
          </button>
          <button
            type="button"
            onClick={() => {
              setActiveMarker((m) => (m === 'cross' ? null : 'cross'));
            }}
            className={`px-3 py-2 rounded-lg text-sm border inline-flex items-center ${
              activeMarker === 'cross' ? 'bg-red-50 border-red-300 text-red-700' : 'border-gray-300 text-gray-700'
            }`}
          >
            <X className="w-4 h-4 mr-1.5" /> Cross
          </button>
          <button
            type="button"
            onClick={() => setActiveMarker((marker) => (marker === 'pen' ? null : 'pen'))}
            className={`px-3 py-2 rounded-lg text-sm border inline-flex items-center ${
              activeMarker === 'pen' ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-300 text-gray-700'
            }`}
          >
            <Pencil className="w-4 h-4 mr-1.5" /> Pen
          </button>
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-gray-200 px-3 py-2">
          <div className="flex items-center gap-2" aria-label="Annotation color">
            {PEN_COLORS.map((color) => (
              <button
                key={color.value}
                type="button"
                onClick={() => setPenColor(color.value)}
                className={`h-6 w-6 rounded-full border-2 ${penColor === color.value ? 'border-sigma-500 ring-2 ring-sigma-200' : 'border-white ring-1 ring-gray-300'}`}
                style={{ backgroundColor: color.value }}
                title={color.label}
                aria-label={`${color.label} annotation color`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={handleRemoveLastPenStroke}
            disabled={currentPenStrokes.length === 0}
            className="text-xs font-medium text-gray-600 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Undo pen
          </button>
        </div>
      </div>

      <div className={`grid min-h-0 flex-1 grid-cols-1 gap-3 ${isScriptFullscreen ? '' : 'xl:grid-cols-4'}`}>
        <div className={`min-h-0 bg-white border border-gray-200 rounded-lg p-3 ${isScriptFullscreen ? '' : 'xl:col-span-3'}`}>
          {isScriptLoading ? (
            <div className="h-[60vh] flex items-center justify-center text-sm text-gray-500">
              Loading script pages...
            </div>
          ) : scriptLoadError ? (
            <div className="h-[60vh] flex items-center justify-center text-sm text-red-600 px-4 text-center">
              {scriptLoadError}
            </div>
          ) : !scriptUrls.length ? (
            <div className="h-[60vh] flex items-center justify-center text-sm text-red-600 px-4 text-center">
              No script pages could be loaded.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2 text-xs text-gray-500">
                  <span>Marker pad:</span>
                  <span className="font-medium text-gray-700">{isMarkerPadPinned ? 'Pinned' : 'Draggable'}</span>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={toggleScriptFullscreen}
                    className="inline-flex items-center rounded-md border border-gray-300 px-2 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    title={isScriptFullscreen ? 'Exit fullscreen' : 'Mark in fullscreen'}
                  >
                    {isScriptFullscreen
                      ? <Minimize2 className="mr-1 h-4 w-4" />
                      : <Maximize2 className="mr-1 h-4 w-4" />}
                    {isScriptFullscreen ? 'Exit full screen' : 'Full screen'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (isBatchQuestionJob) {
                        setCurrentLearnerIndex((i) => Math.max(0, i - 1));
                        return;
                      }
                      setCurrentPageIndex((i) => Math.max(0, i - 1));
                    }}
                    className="p-1.5 border border-gray-300 rounded-md text-gray-700"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-xs text-gray-600">
                    {isBatchQuestionJob
                      ? `Learner ${activeCanvasIndex + 1} / ${activeDisplayCount}`
                      : `Page ${currentPageIndex + 1} / ${totalDisplayPages}`}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      if (isBatchQuestionJob) {
                        setCurrentLearnerIndex((i) => Math.min(activeDisplayCount - 1, i + 1));
                        return;
                      }
                      setCurrentPageIndex((i) => Math.min(totalDisplayPages - 1, i + 1));
                    }}
                    className="p-1.5 border border-gray-300 rounded-md text-gray-700"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className={`relative border border-gray-200 rounded-md overflow-y-auto overflow-x-hidden bg-gray-100 ${
                isScriptFullscreen ? 'h-[calc(100vh-6rem)]' : 'h-[calc(100vh-6.5rem)]'
              }`}>
                <div
                  ref={pageLayerRef}
                  className={`relative w-full bg-white ${activeMarker === 'pen' ? 'cursor-crosshair select-none' : ''}`}
                  style={{ touchAction: activeMarker === 'pen' ? 'none' : 'auto' }}
                  onClick={handleCanvasClick}
                  onPointerDown={handlePenPointerDown}
                  onPointerMove={handlePenPointerMove}
                  onPointerUp={handlePenPointerUp}
                  onPointerCancel={handlePenPointerUp}
                >
                  {isPdfPageLoading && isPdfJob && !displayImageUrl && (
                    <div className="h-[60vh] flex items-center justify-center text-sm text-gray-500">
                      Rendering PDF page...
                    </div>
                  )}
                   {pdfLoadError && isPdfJob && (
                     <div className="h-[60vh] flex items-center justify-center text-sm text-red-600 px-4 text-center">
                       {pdfLoadError}
                     </div>
                   )}
                   {isImageLoading && !isPdfJob && !imageLoadError && (
                     <div className="h-[60vh] flex items-center justify-center text-sm text-gray-500">
                       Loading image...
                     </div>
                   )}
                   {imageLoadError && !isPdfJob && (
                     <div className="h-[60vh] flex items-center justify-center text-sm text-red-600 px-4 text-center">
                       {imageLoadError}
                     </div>
                   )}
                   {!pdfLoadError && !imageLoadError && displayImageUrl && (
                     <img
                       ref={scriptImageRef}
                       src={displayImageUrl}
                       alt={isBatchQuestionJob ? `Learner script ${activeCanvasIndex + 1}` : `Script page ${currentPageIndex + 1}`}
                       className="block w-full h-auto"
                       onLoad={() => {
                         if (!isPdfJob) setIsImageLoading(false);
                       }}
                       onError={() => {
                         if (!isPdfJob) {
                           console.error('Failed to display script image:', currentJob?.scriptFiles?.[activeCanvasIndex]?.path);
                           setIsImageLoading(false);
                           setImageLoadError(`Failed to display script image ${activeCanvasIndex + 1}.`);
                         }
                       }}
                     />
                   )}

                  {currentPenStrokes.length > 0 && (
                    <svg
                      className="pointer-events-none absolute inset-0 h-full w-full"
                      viewBox="0 0 100 100"
                      preserveAspectRatio="none"
                      aria-hidden="true"
                    >
                      {currentPenStrokes.map((stroke) => (
                        <polyline
                          key={stroke.id}
                          points={stroke.points.map((point) => `${point.x},${point.y}`).join(' ')}
                          fill="none"
                          stroke={stroke.color}
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          vectorEffect="non-scaling-stroke"
                        />
                      ))}
                    </svg>
                  )}

                  {currentIcons.map((icon) => (
                    <div
                      key={icon.id}
                      onMouseDown={(e) => handleStartItemDrag(e, { kind: 'icon', id: icon.id, x: icon.x, y: icon.y })}
                      className="group absolute -translate-x-1/2 -translate-y-1/2 cursor-move"
                      style={{ left: `${icon.x}%`, top: `${icon.y}%` }}
                    >
                      {icon.type === 'tick'
                        ? <Check className="w-7 h-7 stroke-[3]" style={{ color: icon.color || '#dc2626' }} />
                        : <X className="w-7 h-7 stroke-[3]" style={{ color: icon.color || '#dc2626' }} />}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveIcon(icon.id);
                        }}
                        className="absolute -top-2 -right-2 hidden group-hover:inline-flex items-center justify-center w-4 h-4 rounded-full bg-white border border-gray-300 text-gray-600 hover:text-red-600"
                        title="Remove marker"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}

                  {currentAnnotations.map((ann) => {
                    const grade = grades.find((g) => g.id === ann.gradeId);
                    if (!grade) return null;
                    return (
                      <div
                        key={ann.id}
                        onMouseDown={(e) => handleStartItemDrag(e, { kind: 'annotation', id: ann.id, x: ann.x, y: ann.y })}
                        className="group absolute -translate-x-1/2 -translate-y-1/2 bg-yellow-200 border border-yellow-500 rounded px-2 py-1 text-xs text-gray-900 cursor-move"
                        style={{ left: `${ann.x}%`, top: `${ann.y}%` }}
                      >
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveAnnotation(ann.id);
                          }}
                          className="absolute -top-2 -right-2 hidden group-hover:inline-flex items-center justify-center w-4 h-4 rounded-full bg-white border border-gray-300 text-gray-600 hover:text-red-600"
                          title="Remove annotation"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                        <div className="font-semibold">Q{grade.questionNumber} {grade.learnerScore}/{grade.totalMarks}</div>
                        {grade.annotation && <div className="italic">{grade.annotation}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        <div className={`${isScriptFullscreen ? 'hidden' : ''} min-h-0 overflow-y-auto bg-white border border-gray-200 rounded-lg p-4 space-y-3`}>
          {currentJob?.workflowSource === 'remark-moderation' && (
            <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-xs text-indigo-900">
              <div className="font-semibold">Remarking / Moderation copy</div>
              <div className="mt-1">
                Previous result:{' '}
                {currentJob?.moderationContext?.previousTotalScore ?? '-'}
                /{currentJob?.moderationContext?.previousMaxScore ?? '-'}
                {currentJob?.moderationContext?.previousPercentage != null
                  ? ` (${currentJob.moderationContext.previousPercentage}%)`
                  : ''}
              </div>
              <div className="mt-1 text-indigo-700">The original completed job will remain unchanged.</div>
            </div>
          )}

          {hasTaskData ? (
            <>
              <h3 className="text-sm font-semibold text-gray-900">Task Information</h3>
              <div className="text-xs text-gray-600 space-y-1">
                <div><span className="font-medium text-gray-800">Task:</span> {currentJob?.taskData?.taskName || '-'}</div>
                <div><span className="font-medium text-gray-800">Type:</span> {currentJob?.taskData?.taskType || '-'}</div>
                <div><span className="font-medium text-gray-800">Subject:</span> {currentJob?.taskData?.subject || currentJob?.subject || '-'}</div>
                <div><span className="font-medium text-gray-800">Grade:</span> {currentJob?.taskData?.grade || currentJob?.grade || '-'}</div>
                <div>
                  <span className="font-medium text-gray-800">Learner:</span>{' '}
                  {isBatchQuestionJob
                    ? `${activeLearner?.learnerName || '-'}${activeLearner?.learnerNumber ? ` (${activeLearner.learnerNumber})` : ''}`
                    : `${currentJob?.learnerName || '-'}${currentJob?.learnerId ? ` (${currentJob.learnerId})` : ''}`}
                </div>
                <div><span className="font-medium text-gray-800">Marker:</span> {currentJob?.markerName || '-'}{currentJob?.markerEmail ? ` (${currentJob.markerEmail})` : ''}</div>
                <div><span className="font-medium text-gray-800">Topics:</span> {(currentJob?.taskData?.topics || []).join(', ') || '-'}</div>
                {currentJob?.questionLabel && (
                  <div><span className="font-medium text-gray-800">Question:</span> Q{currentJob.questionLabel}</div>
                )}
              </div>

              {isBatchQuestionJob && batchQueue.length > 0 && (
                <div className="border-t border-gray-200 pt-3 space-y-2">
                  <label className="text-xs font-semibold text-gray-700">Select Learner</label>
                  <select
                    value={String(activeCanvasIndex)}
                    onChange={(e) => {
                      const next = Number(e.target.value);
                      setCurrentLearnerIndex(Number.isFinite(next) ? next : 0);
                    }}
                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs bg-white"
                  >
                    {batchQueue.map((entry, idx) => (
                      <option key={`${entry.learnerId || 'learner'}-${idx}`} value={idx}>
                        {`${entry.learnerName || `Learner ${idx + 1}`}${entry.learnerNumber ? ` (${entry.learnerNumber})` : ''}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {formError && <p className="text-xs text-red-600">{formError}</p>}

              <div className="border-t border-gray-200 pt-3">
                <h4 className="text-xs font-semibold text-gray-700 mb-2">Select Question/Subquestion</h4>
                <div className="space-y-2 max-h-[45vh] overflow-y-auto">
                  {grades.length === 0 && (
                    <p className="text-xs text-gray-500">No task questions configured.</p>
                  )}
                  {grades.map((grade) => (
                    <button
                      key={grade.id}
                      type="button"
                      onClick={() => setSelectedGradeId(grade.id)}
                      className={`w-full text-left border rounded-md px-2 py-1.5 text-xs ${
                        selectedGradeId === grade.id ? 'border-sigma-500 bg-sigma-50' : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium text-gray-900">
                        Q{grade.questionNumber}: {grade.learnerScore}/{grade.totalMarks} ({grade.percentage}%)
                      </div>
                      {grade.topic && <div className="text-gray-600 mt-0.5">Topic: {grade.topic}</div>}
                      {grade.subtopics?.length > 0 && (
                        <div className="text-gray-500 mt-0.5">Subtopics: {grade.subtopics.join(', ')}</div>
                      )}
                      {grade.concepts?.length > 0 && (
                        <div className="text-gray-500 mt-0.5">
                          Concepts: {grade.concepts.map((c) => `${c.name} (${c.marks})`).join(', ')}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              <h3 className="text-sm font-semibold text-gray-900">Manual Grading</h3>
              {manualQuestionOptions.length > 0 && (
                <select
                  value={selectedGradeId || ''}
                  onChange={(e) => {
                    const nextId = e.target.value || null;
                    setActiveMarker(null);
                    setSelectedGradeId(nextId);
                  }}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                >
                  <option value="">Select saved question...</option>
                  {manualQuestionOptions.map((grade) => (
                    <option key={grade.id} value={grade.id}>
                      {`Q${grade.questionNumber} (${grade.totalMarks})${grade.topic ? ` - ${grade.topic}` : ''}`}
                    </option>
                  ))}
                </select>
              )}
              {markingQuestions.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs text-gray-600">
                    Saved marking questions are loaded below. Click one to prefill the form, adjust the score, then place it on the script.
                  </p>
                  {(targetSubject || targetGrade) && !isQuestionFilterFallback && (
                    <p className="text-xs text-sigma-700">
                      Showing questions for {targetSubject ? `subject: ${currentJob?.taskData?.subject || currentJob?.subject}` : 'all subjects'}
                      {targetGrade ? ` and grade: ${currentJob?.taskData?.grade || currentJob?.grade}` : ''}.
                    </p>
                  )}
                  {isQuestionFilterFallback && (
                    <p className="text-xs text-amber-700">
                      No saved questions matched this subject/grade yet, so the full question bank is shown.
                    </p>
                  )}
                </div>
              )}
              <p className="text-xs text-gray-600">
                Save or select a question, then click on the script to place its sticky note. Drag an existing note to reposition it.
              </p>
              <input
                type="text"
                placeholder="Question (e.g., 1a)"
                value={questionNumber}
                onChange={(e) => setQuestionNumber(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  placeholder="Score"
                  value={learnerScore}
                  onChange={(e) => setLearnerScore(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
                <input
                  type="number"
                  placeholder="Total"
                  value={totalMarks}
                  onChange={(e) => setTotalMarks(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <textarea
                rows={3}
                placeholder="Annotation (optional)"
                value={annotation}
                onChange={(e) => setAnnotation(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm resize-y"
              />
              {formError && <p className="text-xs text-red-600">{formError}</p>}
              <button
                type="button"
                onClick={handleSaveGrade}
                className="w-full px-3 py-2 bg-sigma-600 text-white text-sm rounded-md hover:bg-sigma-700"
              >
                Save Grade & Arm Note Placement
              </button>

              <div className="border-t border-gray-200 pt-3">
                <h4 className="text-xs font-semibold text-gray-700 mb-2">
                  {markingQuestions.length > 0 ? 'Available Questions / Saved Grades' : 'Saved Grades (click to place)'}
                </h4>
                <div className="space-y-2 max-h-[45vh] overflow-y-auto">
                  {grades.length === 0 && (
                    <p className="text-xs text-gray-500">
                      {markingQuestions.length > 0 ?
                        'No saved questions available for this job yet.' :
                        'No saved questions loaded yet. Create one in Task Manager or refresh this job.'}
                    </p>
                  )}
                  {grades.map((grade) => (
                    <button
                      key={grade.id}
                      type="button"
                      onClick={() => {
                        setActiveMarker(null);
                        setSelectedGradeId((prev) => (prev === grade.id ? null : grade.id));
                      }}
                      className={`w-full text-left border rounded-md px-2 py-1.5 text-xs ${
                        selectedGradeId === grade.id ? 'border-sigma-500 bg-sigma-50' : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium text-gray-900">
                        Q{grade.questionNumber}: {grade.learnerScore}/{grade.totalMarks} ({grade.percentage}%)
                      </div>
                      {grade.annotation && <div className="text-gray-600 mt-0.5">{grade.annotation}</div>}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManualMarkingWorkspace;
